import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarClock, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

const TypeRow = ({ t, onChange, onRemove, canRemove }) => {
  const set = (patch) => onChange({ ...t, ...patch });
  const toggleDay = (d) => set({ days: t.days.includes(d) ? t.days.filter((x) => x !== d) : [...t.days, d].sort() });
  return (
    <div className="border border-slate-200 rounded-lg p-4 space-y-3 bg-slate-50/60" data-testid={`scheduling-type-${t.key}`}>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
        <div className="col-span-2">
          <Label>Type name</Label>
          <Input value={t.label} onChange={(e) => set({ label: e.target.value })} data-testid={`scheduling-type-${t.key}-label`} />
        </div>
        <div>
          <Label>Duration (min)</Label>
          <Input type="number" min="5" step="5" value={t.duration} onChange={(e) => set({ duration: Number(e.target.value) })} data-testid={`scheduling-type-${t.key}-duration`} />
        </div>
        <div>
          <Label>Opens</Label>
          <Input type="time" value={t.start} onChange={(e) => set({ start: e.target.value })} data-testid={`scheduling-type-${t.key}-start`} />
        </div>
        <div>
          <Label>Last end</Label>
          <Input type="time" value={t.end} onChange={(e) => set({ end: e.target.value })} data-testid={`scheduling-type-${t.key}-end`} />
        </div>
        <div>
          <Label>Slot step</Label>
          <Select value={String(t.step)} onValueChange={(v) => set({ step: Number(v) })}>
            <SelectTrigger data-testid={`scheduling-type-${t.key}-step`}><SelectValue /></SelectTrigger>
            <SelectContent>{[15, 30, 60].map((s) => <SelectItem key={s} value={String(s)}>{s} min</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-slate-500 mr-1">Bookable days</span>
        {DAYS.map((d, i) => (
          <button type="button" key={d} onClick={() => toggleDay(i)} data-testid={`scheduling-type-${t.key}-day-${i}`}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${t.days.includes(i) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-500 border-slate-300 hover:border-blue-400"}`}>{d}</button>
        ))}
        {canRemove && <Button type="button" variant="ghost" size="sm" className="ml-auto text-red-600 hover:bg-red-50" onClick={onRemove} data-testid={`scheduling-type-${t.key}-remove`}><Trash2 className="w-4 h-4 mr-1" /> Remove</Button>}
      </div>
    </div>
  );
};

export const SchedulingSettings = () => {
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => { const { data } = await axios.get(`${API}/scheduling-settings`); setSaved(data); setForm(data); };
  useEffect(() => { load().catch(() => toast.error("Could not load scheduling settings")); }, []);
  if (!form) return null;

  const setType = (i, t) => setForm({ ...form, types: form.types.map((x, j) => (j === i ? t : x)) });
  const addType = () => {
    const label = `New type ${form.types.length + 1}`;
    setForm({ ...form, types: [...form.types, { key: slug(label), label, duration: 30, start: "09:00", end: "17:00", days: [0, 1, 2, 3, 4], step: 30 }] });
  };
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/scheduling-settings`, form);
      setSaved(data); setForm(data);
      toast.success("Scheduling hours saved — the phone agent will only offer these slots");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };
  const dirty = JSON.stringify(form) !== JSON.stringify(saved);

  return (
    <Card className="bg-white border-0 shadow-lg" data-testid="scheduling-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><CalendarClock className="w-5 h-5 text-blue-600" /> Bookable days &amp; hours</CardTitle>
        <p className="text-sm text-slate-600">Each appointment type has its own hours. The Retell phone agent only offers and accepts slots inside these hours; the in-app form shows a warning but lets you override. Times are {form.timezone}.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {form.types.map((t, i) => <TypeRow key={t.key} t={t} onChange={(nt) => setType(i, nt)} onRemove={() => setForm({ ...form, types: form.types.filter((_, j) => j !== i) })} canRemove={form.types.length > 1} />)}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button type="button" variant="outline" size="sm" onClick={addType} data-testid="scheduling-add-type"><Plus className="w-4 h-4 mr-1" /> Add appointment type</Button>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Switch checked={form.allow_overlap_unassigned} onCheckedChange={(v) => setForm({ ...form, allow_overlap_unassigned: v })} data-testid="scheduling-allow-overlap" />
            Allow two unassigned appointments at the same time
          </label>
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={busy || !dirty} className="bg-blue-600 hover:bg-blue-700" data-testid="scheduling-save-button"><Save className="w-4 h-4 mr-1" /> {busy ? "Saving…" : "Save hours"}</Button>
        </div>
      </CardContent>
    </Card>
  );
};
